/** @format */

import { Injectable } from '@angular/core';

export enum Stat {
	NewYearsDay = 'New Years Day',
	FamilyDay = 'Family Day',
	GoodFriday = 'Good Friday',
	EasterMonday = 'Easter Monday',
	VictoriaDay = 'Victoria Day',
	CanadaDay = 'Canada Day',
	BCDay = 'BC Day',
	LabourDay = 'Labour Day',
	NationalDayOfMourning = 'National Day of Mourning',
	NationalDayForTruthandReconciliation = 'National Day for Truth and Reconciliation',
	ThanksgivingDay = 'Thanksgiving Day',
	RemembranceDay = 'Remembrance Day',
	ChristmasDay = 'Christmas Day',
	BoxingDay = 'Boxing Day',
}

// Date type uses 0-based months, so this enum is used for code readability
export enum Month {
	Jan,
	Feb,
	Mar,
	Apr,
	May,
	Jun,
	Jul,
	Aug,
	Sep,
	Oct,
	Nov,
	Dec,
}

export type StatHoliday = {
	name: string;
	date: Date;
	observedDate: Date;
};

@Injectable({
	providedIn: 'root',
})
export class StatCalculatorService {
	getStatHoliday(stat: Stat, year: number): StatHoliday | undefined {
		// To get accurate observed date, all stats must be calcualted first
		const stats = this.getStatHolidays(year);
		return stats.find((d) => d.name == stat);
	}

	getStatHolidays(year: number): StatHoliday[] {
		if (year < 2019) {
			throw new RangeError('Results invalid prior to 2019');
		}

		const stats: StatHoliday[] = [];

		const pushStatHoliday = (name: Stat, date: Date, yearStart: number | null = null, yearEnd: number | null = null) => {
			if ((yearStart && year < yearStart) || (yearEnd && year > yearEnd)) {
				return; // This holiday doesn't apply this year
			}

			stats.push({
				name: name,
				date: date,
				observedDate: this.nextWorkDay(date, stats),
			});
		};

		// In chronological order so that the observed date can be calculated correctly
		pushStatHoliday(Stat.NewYearsDay, new Date(year, Month.Jan, 1));
		pushStatHoliday(Stat.FamilyDay, this.nthMonday(year, Month.Feb, 3), 2019); // 3rd Monday started 2019
		pushStatHoliday(Stat.GoodFriday, this.offsetDate(this.getEasterSunday(year), -2));
		pushStatHoliday(Stat.EasterMonday, this.offsetDate(this.getEasterSunday(year), 1));
		pushStatHoliday(Stat.VictoriaDay, this.getVictoriaDay(year));
		pushStatHoliday(Stat.CanadaDay, new Date(year, Month.Jul, 1));
		pushStatHoliday(Stat.BCDay, this.nthMonday(year, Month.Aug, 1));
		pushStatHoliday(Stat.LabourDay, this.nthMonday(year, Month.Sep, 1));
		pushStatHoliday(Stat.NationalDayOfMourning, new Date(year, Month.Sep, 19), 2022, 2022); // 2022 Only
		pushStatHoliday(Stat.NationalDayForTruthandReconciliation, new Date(year, Month.Sep, 30), 2021); // Started 2021
		pushStatHoliday(Stat.ThanksgivingDay, this.nthMonday(year, Month.Oct, 2));
		pushStatHoliday(Stat.RemembranceDay, new Date(year, Month.Nov, 11));
		pushStatHoliday(Stat.ChristmasDay, new Date(year, Month.Dec, 25));
		pushStatHoliday(Stat.BoxingDay, new Date(year, Month.Dec, 26));

		return stats;
	}

	private offsetDate(date: Date, daysOffset: number): Date {
		const d = new Date(date.getTime());
		d.setDate(d.getDate() + daysOffset);
		return d;
	}

	private nthMonday(year: number, month: number, n: number): Date {
		const first = new Date(year, month, 1);
		const d = first.getDay();
		const firstMonday = ((7 - ((d + 6) % 7)) % 7) + 1;
		return new Date(year, month, firstMonday + (n - 1) * 7);
	}

	private getEasterSunday(year: number): Date {
		// 1961 Nature algorithm for Easter Sunday
		// https://en.wikipedia.org/wiki/Date_of_Easter
		const a = year % 19;
		const b = Math.floor(year / 100);
		const c = year % 100;
		const d = Math.floor(b / 4);
		const e = b % 4;
		const i = Math.floor(c / 4);
		const k = c % 4;
		const g = Math.floor((8 * b + 13) / 25);
		const h = (19 * a + b - d - g + 15) % 30;
		const l = (2 * e + 2 * i - k + 32 - h) % 7;
		const m = Math.floor((a + 11 * h + 19 * l) / 433);
		const n = Math.floor((h + l - 7 * m + 90) / 25);
		const p = (h + l - 7 * m + 33 * n + 19) % 32;
		return new Date(year, n - 1, p);
	}

	private getVictoriaDay(year: number): Date {
		// Last Monday prior to May 25
		const d = new Date(year, Month.May, 24);
		return this.offsetDate(d, (-1 * (d.getDay() + 6)) % 7);
	}

	private isWeekend(date: Date): boolean {
		return date.getDay() == 0 || date.getDay() == 6;
	}

	private nextWorkDay(date: Date, currentStats: StatHoliday[]) {
		let t = date;
		for (; this.isWeekend(t) || this.isStatHoliday(t, currentStats); t = this.offsetDate(t, 1)) {}
		return t;
	}

	private isStatHoliday(date: Date, currentStats: StatHoliday[]): boolean {
		return currentStats.find((d) => d.observedDate.getTime() === date.getTime()) !== undefined;
	}
}
